import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, MailCheck, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SectionHeading } from "@/components/Reveal";
import { CITIES, COMPANY_NAME, P_IVA } from "@/constants/brand";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-surface border border-input focus:border-brand rounded-xl px-4 py-3.5 text-white placeholder:text-dim outline-none transition-colors duration-300 text-base";

const INITIAL = { nome: "", cognome: "", email: "", telefono: "", oggetto: "", messaggio: "" };

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="block text-sm font-bold text-white mb-2">{label}</label>
    <input data-testid={testid} className={inputCls} {...props} />
  </div>
);

export default function ContactPage() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact-messages", form);
      setDone(true);
      toast.success("Messaggio inviato! Ti risponderemo al più presto.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-obsidian text-white min-h-screen">
      <Navbar />
      <main className="pt-32 pb-24 px-5 sm:px-8 relative overflow-hidden">
        <div className="hero-glow w-[420px] h-[420px] bg-brand/15 -top-24 -right-32" />
        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-[1fr_0.7fr] gap-14">
          <div>
            <SectionHeading
              eyebrow="Contatti"
              title="Parliamone di persona"
              sub="Hai domande sul lavoro, sulle città coperte o su altro? Scrivici: ti risponde una persona vera."
              testid="contact-title"
            />

            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="ok"
                  data-testid="contact-success"
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-card2 border border-brand/40 rounded-3xl p-10 text-center"
                >
                  <div className="w-14 h-14 mx-auto rounded-full bg-brand/15 border border-brand/40 flex items-center justify-center mb-5">
                    <MailCheck className="w-6 h-6 text-brand" />
                  </div>
                  <h3 className="font-display text-2xl font-extrabold text-white mb-3">Messaggio inviato!</h3>
                  <p className="text-muted2">Grazie per averci scritto. Ti ricontatteremo il prima possibile.</p>
                  <button
                    data-testid="contact-reset-btn"
                    onClick={() => { setForm(INITIAL); setDone(false); }}
                    className="mt-7 inline-flex items-center gap-2 border border-brand/40 hover:border-brand hover:bg-cardhover text-white font-bold px-6 py-3 rounded-full transition-colors duration-300"
                  >
                    Invia un altro messaggio
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="contact-form"
                  onSubmit={submit}
                  initial={{ opacity: 0, y: 32 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-card2/70 backdrop-blur-md border border-brand/20 rounded-3xl p-6 sm:p-9 space-y-6"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Nome *" testid="contact-nome-input" required value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Mario" />
                    <Field label="Cognome *" testid="contact-cognome-input" required value={form.cognome} onChange={(e) => set("cognome", e.target.value)} placeholder="Rossi" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Email *" testid="contact-email-input" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="mario.rossi@email.it" />
                    <Field label="Telefono" testid="contact-telefono-input" type="tel" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="+39 333 1234567" />
                  </div>
                  <Field label="Oggetto *" testid="contact-oggetto-input" required value={form.oggetto} onChange={(e) => set("oggetto", e.target.value)} placeholder="Di cosa vuoi parlarci?" />
                  <div>
                    <label className="block text-sm font-bold text-white mb-2">Messaggio *</label>
                    <textarea
                      data-testid="contact-messaggio-input"
                      required
                      rows={5}
                      value={form.messaggio}
                      onChange={(e) => set("messaggio", e.target.value)}
                      placeholder="Scrivi qui il tuo messaggio..."
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                  <motion.button
                    data-testid="submit-messaggio-btn"
                    type="submit"
                    disabled={sending}
                    whileTap={{ scale: 0.97 }}
                    className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hot disabled:opacity-60 text-white font-display font-bold text-lg px-8 py-4 rounded-full transition-colors duration-300 shadow-[0_10px_30px_-5px_rgba(232,67,147,0.45)]"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" /> Invio in corso...
                      </>
                    ) : (
                      "Invia Messaggio"
                    )}
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          <motion.aside
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-5 lg:pt-32"
          >
            <div data-testid="contact-info-card" className="bg-card2 border border-brand/20 rounded-3xl p-8">
              <div className="w-12 h-12 rounded-2xl bg-brand/15 border border-brand/30 flex items-center justify-center mb-6">
                <Building2 className="w-5 h-5 text-brand" />
              </div>
              <h3 className="font-display text-xl font-bold text-white mb-3">{COMPANY_NAME}</h3>
              <p className="text-sm text-muted2 leading-relaxed">
                P.IVA {P_IVA}
                <br />
                Titolare del trattamento dei dati e gestore della piattaforma Io Amo La Mia Città.
              </p>
            </div>
            <div data-testid="contact-cities-card" className="bg-card2 border border-brand/20 rounded-3xl p-8">
              <div className="w-12 h-12 rounded-2xl bg-brand/15 border border-brand/30 flex items-center justify-center mb-6">
                <MapPin className="w-5 h-5 text-brand" />
              </div>
              <h3 className="font-display text-xl font-bold text-white mb-4">Le nostre città</h3>
              <div className="flex flex-col gap-2.5">
                {CITIES.map((c) => (
                  <span key={c} className="inline-flex items-center gap-2 text-sm font-semibold text-muted2">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand" /> {c}
                  </span>
                ))}
              </div>
            </div>
          </motion.aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}
